import { BEIGE_400, BLUE_1000, BLUE_200, BLUE_400, GRAY_300, GRAY_900 } from 'constants/styling/theme'
import { Box, Stack, Typography } from '@mui/material'
import { FC, useEffect, useRef, useState } from 'react'
import { LabCombinedSettings, LabEnhanceMode, LabEnhanceSettings, LabStageSettings } from './labSdk.types'
import { LabListSection, LabSection as LabSectionKey, LabSectionState, initialLabSections, resolveLabAllFeaturesBlocker, resolveLabCanvasSection, toLabCombinedSettings, toggleLabSection } from './labAllFeatures'
import { PhotoStudioSidebarOption, isAdjustEditorOption, useClientPhotoStudio } from '../ClientPhotoStudio.context'

import { AdjustPanel } from '../AdjustPanel'
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined'
import BlurOnIcon from '@mui/icons-material/BlurOn'
import { BlurPanel } from '../adjust/BlurPanel.component'
import ChairOutlinedIcon from '@mui/icons-material/ChairOutlined'
import { EraserInstructions } from '../MagicEraserPanel/EraserInstructions.component'
import { GenerateButton } from '../common/GenerateButton'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { LabAiLabelSection } from './LabAiLabelSection.component'
import { LabEnhanceOptions } from './LabEnhanceOptions.component'
import { LabRunSource } from './labRunSource'
import { LabSection } from './LabPanelPrimitives.component'
import { LabStageOptions } from './LabStageOptions.component'
import { LabSubscribeButton, LabSubscribersOnlyCard } from './LabSubscribe.component'
import { MUIButton } from 'components/common/MUIButton'
import PanoramaOutlinedIcon from '@mui/icons-material/PanoramaOutlined'
import PhotoFilterOutlinedIcon from '@mui/icons-material/PhotoFilterOutlined'
import { useLabRunSourceOptions } from './useLabRunSourceOptions.hook'
import { useTranslation } from 'react-i18next'
import { useWorkspacePlan } from 'utils/hooks'

interface LabAllFeaturesPanelProps {
  isDisabled?: boolean
  isProcessingImage?: boolean
  /** Erase: a new mask (or object) is marked on the photo since the last erase */
  canErase: boolean
  /** `source` is the Generate chevron's pick (After when the main CTA was clicked) */
  onGenerate: (settings: LabCombinedSettings, source?: LabRunSource) => void
}

const SECTION_OF_TOOL: Record<PhotoStudioSidebarOption, LabListSection | 'blur'> = {
  [PhotoStudioSidebarOption.STAGING]: 'stage',
  [PhotoStudioSidebarOption.ENHANCE]: 'enhance',
  [PhotoStudioSidebarOption.ERASER]: 'erase',
  [PhotoStudioSidebarOption.BLUR]: 'blur',
  [PhotoStudioSidebarOption.ADJUST]: 'adjust',
}

const TOOL_OF_SECTION: Record<LabSectionKey, PhotoStudioSidebarOption> = {
  stage: PhotoStudioSidebarOption.STAGING,
  enhance: PhotoStudioSidebarOption.ENHANCE,
  erase: PhotoStudioSidebarOption.ERASER,
}

const SECTION_TITLE_KEYS: Record<LabListSection, string> = {
  stage: 'sidebar_menu.staging',
  enhance: 'sidebar_menu.enhance',
  erase: 'sidebar_menu.eraser',
  adjust: 'sidebar_menu.adjust',
}

const SECTIONS: LabSectionKey[] = ['stage', 'enhance', 'erase']

// The catalogue's `sidebar_menu` group replaces the bundled one whole: Pintura's Blur label is already in it
const BLUR_TITLE_KEY = 'manual_tools.BLUR'

/** How long "Erase is now off…" stays, with its Undo */
const NOTICE_DURATION_MS = 10000

const LabSectionNotice: FC<{ text: string, undoLabel: string, onUndo: () => void }> = ({ text, undoLabel, onUndo }) => (
  <Stack role="status" flexDirection="row" alignItems="flex-start" gap="1rem" sx={{ padding: '1rem 1.2rem', marginBottom: '1.6rem', borderRadius: '0.8rem', backgroundColor: BLUE_200, border: `1px solid ${BLUE_400}` }}>

    <InfoOutlinedIcon sx={{ fontSize: '1.8rem', color: BLUE_1000, marginTop: '0.1rem' }} />

    <Typography sx={{ flex: 1, fontSize: '1.3rem', lineHeight: 1.45, letterSpacing: '-0.3px', color: GRAY_900 }}>{text}</Typography>

    <Box component="button" type="button" onClick={onUndo} sx={{ font: 'inherit', fontSize: '1.3rem', fontWeight: 550, color: BLUE_1000, background: 'none', border: 0, padding: 0, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px', flexShrink: 0 }}>
      {undoLabel}
    </Box>

  </Stack>
)

const stageStyleLabel = (settings: LabStageSettings, t: (key: string) => string) => {
  if (settings.customStyle) return t('lab.stage.style.custom')
  return settings.style === 'keep' ? t('lab.stage.keep_as_is') : t(`lab.stage.style.${settings.style}`)
}

const stageSummary = (settings: LabStageSettings, t: (key: string) => string) => settings.mode === 'indoor_staging'
  ? `${t('lab.stage.mode.indoor_staging')} · ${stageStyleLabel(settings, t)}`
  : t(`lab.stage.mode.${settings.mode}`)

const enhanceFeatureCount = (settings: LabEnhanceSettings) => [settings.perspective, settings.tonecraft, settings.privacy, settings.replaceSky].filter(Boolean).length

/**
 * "Display all features at once" (Lab SDK engine): Stage, Enhance, Erase and Adjust as collapsible sections of one
 * list, combined into one Generate — the Lab Playground's model. A section's switch puts it in the next run; changing
 * an option of a section that is off turns it on. Stage and Erase are both generative edits and the Lab runs one per
 * order, so turning one on turns the other off (with Undo). Adjust has no switch: its Pintura tools apply instantly.
 *
 * The canvas follows the sections through the Studio's active tool: the eraser's mask drawer while Erase is on, the
 * Adjust editor while Blur or an Adjust tool is on it (Generate first commits their edits). Blur has its own section,
 * with the in-house Adjust editor only: expanding it puts Blur on the canvas, and it closes when Blur leaves it.
 *
 * Non-subscribers get the list locked: the Subscribers only card on top, every switch off and disabled, the options
 * disabled (sections still expand, to show what is there), Subscribe in Generate's place.
 */
export const LabAllFeaturesPanel: FC<LabAllFeaturesPanelProps> = ({ isDisabled = false, isProcessingImage = false, canErase, onGenerate }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { activeFeature, switchEditingMode, isToolSwitching, hasAIEraserCompleted, labStageSettings, labEnhanceSettings, updateLabStageSettings, updateLabEnhanceSettings, labSectionsOpen, setLabSectionsOpen, isPinturaReplacement } = useClientPhotoStudio()
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()
  const isLocked = !hasWorkspaceActiveSubscription

  // Seeded once from the tool on screen: switching the view on changes nothing about the next Generate. Expanded
  // sections: only Stage the first time in the session, then as the list was left (kept in the Photo Studio context)
  const [initialSections] = useState(() => initialLabSections(SECTION_OF_TOOL[activeFeature], labSectionsOpen))
  const [on, setOn] = useState<LabSectionState>(initialSections.on)
  const [open, setOpen] = useState(initialSections.open)
  useEffect(() => {
    setLabSectionsOpen(open)
  }, [open, setLabSectionsOpen])
  const [notice, setNotice] = useState<{ section: LabSectionKey, turnedOff: LabSectionKey, previous: LabSectionState } | null>(null)

  const isBusy = isProcessingImage || isDisabled || isToolSwitching
  // The Adjust editor is on the canvas (Blur or an Adjust tool): the canvas stays, and Generate first commits its edits
  const isAdjusting = isAdjustEditorOption(activeFeature)
  const canvasTool = TOOL_OF_SECTION[resolveLabCanvasSection(on)]

  useEffect(() => {
    if (!notice) return
    const timer = window.setTimeout(() => setNotice(null), NOTICE_DURATION_MS)
    return () => window.clearTimeout(timer)
  }, [notice])

  // The canvas follows the sections (mask drawer while Erase is on); an open Adjust tool keeps Pintura until "Done"
  useEffect(() => {
    if (isAdjusting || isToolSwitching || isProcessingImage || activeFeature === canvasTool) return
    // A failed switch is already reported (toast + Sentry) by the context's auto-save
    switchEditingMode(canvasTool).catch(() => undefined)
  }, [activeFeature, canvasTool, isAdjusting, isProcessingImage, isToolSwitching, switchEditingMode])

  const setSectionOpen = (section: LabListSection, value: boolean) => setOpen((previous) => ({ ...previous, [section]: value }))
  const endEditing = () => switchEditingMode(canvasTool).catch(() => undefined)
  /** Collapsing Adjust while one of its tools is on the canvas ends adjusting, like "Done adjusting" (its edits are kept) */
  const setAdjustOpen = (value: boolean) => {
    setSectionOpen('adjust', value)
    if (!value && activeFeature === PhotoStudioSidebarOption.ADJUST) endEditing()
  }

  // Blur is one tool, so its section IS the tool: expanding it puts Blur on the canvas, collapsing it (or "Done blurring",
  // or Generate) takes it off, and the section closes whenever Blur leaves the canvas. Locked, it only shows its options.
  const isBlurring = activeFeature === PhotoStudioSidebarOption.BLUR
  const [isBlurExpanded, setIsBlurExpanded] = useState(false)
  const wasBlurringRef = useRef(isBlurring)
  useEffect(() => {
    if (wasBlurringRef.current && !isBlurring) setIsBlurExpanded(false)
    wasBlurringRef.current = isBlurring
  }, [isBlurring])
  const setBlurOpen = (value: boolean) => {
    setIsBlurExpanded(value)
    if (value && !isLocked) switchEditingMode(PhotoStudioSidebarOption.BLUR).catch(() => setIsBlurExpanded(false))
    if (!value && isBlurring) endEditing()
  }

  const setSectionOn = (section: LabSectionKey, value: boolean) => {
    const result = toggleLabSection(on, section, value)
    setOn(result.on)
    setNotice(result.turnedOff ? { section, turnedOff: result.turnedOff, previous: on } : null)
    // A section switched on opens to show its options; switched off, it collapses to its summary
    setSectionOpen(section, value)
  }

  /** Changing an option of a section that is off turns it on (Playground: picking a look turns Tonecraft on) */
  const turnOnForEdit = (section: LabSectionKey) => {
    if (!on[section]) setSectionOn(section, true)
  }

  const settings = toLabCombinedSettings(on, labStageSettings, labEnhanceSettings)
  const blocker = resolveLabAllFeaturesBlocker({ on, stage: labStageSettings, enhance: labEnhanceSettings, canErase, hasErased: hasAIEraserCompleted, isBusy })

  // Generate while an Adjust tool is open: first commit the Adjust edits — the same save as "Done adjusting", the
  // section stays expanded — then run on the adjusted photo, once the Studio has re-rendered with it (running in the
  // same click would start from the photo as it was before the Adjust edits)
  const [pendingRun, setPendingRun] = useState<LabCombinedSettings | null>(null)
  const generate = () => {
    if (!isAdjusting) {
      onGenerate(settings)
      return
    }
    setPendingRun(settings)
    switchEditingMode(canvasTool).catch(() => setPendingRun(null))
  }
  useEffect(() => {
    if (!pendingRun || isAdjusting || isToolSwitching) return
    setPendingRun(null)
    onGenerate(pendingRun)
  }, [pendingRun, isAdjusting, isToolSwitching, onGenerate])
  // The eraser's mask is drawn on the image on screen, so a run with Erase always starts from it: no chevron
  const sourceOptions = useLabRunSourceOptions((source) => onGenerate(settings, source))
  const sectionsOn = SECTIONS.filter((section) => on[section])

  const sectionNotice = (section: LabSectionKey) => notice?.section === section && (
    <LabSectionNotice
      text={t(notice.turnedOff === 'erase' ? 'lab.all_features.erase_turned_off' : 'lab.all_features.stage_turned_off')}
      undoLabel={t('lab.all_features.undo')}
      onUndo={() => {
        setOn(notice.previous)
        setNotice(null)
      }}
    />
  )

  const sectionProps = (section: LabSectionKey) => ({
    title: t(SECTION_TITLE_KEYS[section]),
    open: open[section],
    onOpenChange: (value: boolean) => setSectionOpen(section, value),
    on: !isLocked && on[section],
    onToggle: (value: boolean) => setSectionOn(section, value),
    switchLabel: t('lab.all_features.include', { section: t(SECTION_TITLE_KEYS[section]) }),
    disabled: isBusy || isLocked,
  })

  return (
    <Stack sx={{ height: '100%', maxHeight: '100%', minHeight: 0, overflow: 'hidden' }}>

      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          // The 8px scrollbar is always reserved: expanding a section must not shift the switches sideways
          scrollbarGutter: 'stable',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: BEIGE_400, borderRadius: '4px' },
        }}
      >

        {isLocked && <LabSubscribersOnlyCard margin="1.6rem 1.6rem 0.8rem 1.4rem" />}

        <LabSection first icon={<ChairOutlinedIcon />} summary={stageSummary(labStageSettings, t)} {...sectionProps('stage')}>
          {sectionNotice('stage')}
          <LabStageOptions settings={labStageSettings} onChange={(patch) => { updateLabStageSettings(patch); turnOnForEdit('stage') }} disabled={isBusy || isLocked} />

          {/* The AI label (EU AI Act) under the Stage options, as in the One tool Stage panel — staging is where it matters most */}
          <Box sx={{ borderTop: `1px solid ${GRAY_300}`, paddingTop: '1.2rem', marginTop: '2.4rem' }}>
            <LabAiLabelSection isDisabled={isBusy || isLocked} />
          </Box>

        </LabSection>

        <LabSection
          icon={<PhotoFilterOutlinedIcon />}
          summary={labEnhanceSettings.mode === LabEnhanceMode.BASIC
            ? [t('lab.all_features.summary.auto_enhance'), ...(labEnhanceSettings.replaceSky ? [t('lab.feature.replace_sky')] : [])].join(' · ')
            : t('lab.all_features.summary.advanced', { count: enhanceFeatureCount(labEnhanceSettings) })}
          {...sectionProps('enhance')}
        >
          <LabEnhanceOptions settings={labEnhanceSettings} onChange={(patch) => { updateLabEnhanceSettings(patch); turnOnForEdit('enhance') }} disabled={isBusy || isLocked} />
        </LabSection>

        <LabSection icon={<AutoFixHighOutlinedIcon />} summary={t(canErase ? 'lab.all_features.summary.erase_marked' : 'lab.all_features.summary.erase_empty')} {...sectionProps('erase')}>
          {sectionNotice('erase')}
          <EraserInstructions />
        </LabSection>

        {/* Blur and Adjust: they apply instantly and are never part of the Generate run the sections above describe */}
        {isPinturaReplacement && (
          <LabSection icon={<BlurOnIcon />} title={t(BLUR_TITLE_KEY)} summary={t('blur_instructions.title')} open={isBlurring || isBlurExpanded} onOpenChange={setBlurOpen}>

            <BlurPanel embedded />

            {isBlurring && (
              <MUIButton type="secondaryBorder" onClick={endEditing} disabled={isToolSwitching} sx={{ marginTop: '1.2rem' }}>
                {t('lab.all_features.done_blurring')}
              </MUIButton>
            )}

          </LabSection>
        )}

        <LabSection icon={<PanoramaOutlinedIcon />} title={t(SECTION_TITLE_KEYS.adjust)} summary={t('lab.all_features.summary.adjust_tools')} open={open.adjust} onOpenChange={setAdjustOpen}>

          <AdjustPanel embedded beforeToolSelect={() => switchEditingMode(PhotoStudioSidebarOption.ADJUST)} />

          {activeFeature === PhotoStudioSidebarOption.ADJUST && (
            <MUIButton type="secondaryBorder" onClick={endEditing} disabled={isToolSwitching} sx={{ marginTop: '1.2rem' }}>
              {t('lab.all_features.done_adjusting')}
            </MUIButton>
          )}

        </LabSection>

      </Stack>

      {/* GenerateButton bleeds over its parent's padding to draw its top hairline edge to edge */}
      <Box sx={{ padding: '0 2.3rem' }}>

        {isLocked && <LabSubscribeButton />}

        {!isLocked && (
          <GenerateButton
            onGenerate={generate}
            sourceOptions={on.erase ? undefined : sourceOptions}
            disabled={Boolean(blocker)}
            disabledTooltip={blocker ? t(blocker) : undefined}
            helpPlacement="none"
            caption={sectionsOn.length > 0 ? t('lab.all_features.recap', { features: sectionsOn.map((section) => t(SECTION_TITLE_KEYS[section])).join(' + ') }) : undefined}
          />
        )}

      </Box>

    </Stack>
  )
}
